import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';

class OTPModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            otp: ['', '', '', '', '', ''],
            isVerifying: false,
            isResetting: false,
            mode: 'authenticator' // 'authenticator' | 'email'
        };
        this.otpRefs = [];
        
        // Flag to track if email was sent in this session
        this.emailSentInSession = false;
        
        // Flag to prevent double API calls (synchronous check)
        this.isSendingEmail = false;
    }

    componentDidMount() {
        // Focus on first input when modal opens
        if (this.props.show && this.otpRefs[0]) {
            setTimeout(() => this.otpRefs[0].focus(), 100);
        }
    }

    componentDidUpdate(prevProps) {
        // Focus on first input when modal opens
        if (this.props.show && !prevProps.show && this.otpRefs[0]) {
            setTimeout(() => this.otpRefs[0].focus(), 100);
        }
        
        // If viaEmail prop changes to true, switch mode
        if (this.props.viaEmail && !prevProps.viaEmail) {
            this.setState({ mode: 'email' });
        }
        
        // If modal just opened with viaEmail true, switch mode
        if (this.props.show && !prevProps.show && this.props.viaEmail) {
            this.setState({ mode: 'email' });
        }
        
        // Reset flags when modal closes
        if (!this.props.show && prevProps.show) {
            this.emailSentInSession = false;
            this.isSendingEmail = false;
        }
    }

    handleOtpChange = (index, value) => {
        // Only allow numbers
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...this.state.otp];
        newOtp[index] = value;

        this.setState({ otp: newOtp });

        // Auto-focus next input
        if (value && index < 5) {
            this.otpRefs[index + 1].focus();
        }
    };

    handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !this.state.otp[index] && index > 0) {
            // Focus previous input on backspace if current is empty
            this.otpRefs[index - 1].focus();
        } else if (e.key === 'Enter') {
            this.handleVerifyOtp();
        }
    };

    handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text/plain');
        const digits = pasteData.replace(/\D/g, '').slice(0, 6);
        
        if (digits.length > 0) {
            const newOtp = [...this.state.otp];
            for (let i = 0; i < 6; i++) {
                newOtp[i] = digits[i] || '';
            }
            this.setState({ otp: newOtp });
            
            // Focus on the next empty field or last field
            const nextIndex = Math.min(digits.length, 5);
            this.otpRefs[nextIndex].focus();
        }
    };

    // Get the auth method based on current mode
    getAuthMethod = () => {
        if (this.props.viaEmail || this.state.mode === 'email') {
            return 'email';
        }
        return 'google';
    };

    handleVerifyOtp = () => {
        const otpString = this.state.otp.join('');
        if (otpString.length === 6) {
            this.setState({ isVerifying: true });
            
            const authMethod = this.getAuthMethod();
            
            this.props.onVerifyOtp(otpString, authMethod);
        }
        this.setState({ 
            otp: ['', '', '', '', '', ''],
            isVerifying: false 
        });
        this.props.onClose();
    };

    handleResetOtp = () => {
        this.setState({ isResetting: true });
        this.props.onResetOtp();
    };

    handleClose = () => {
        this.setState({ 
            otp: ['', '', '', '', '', ''],
            isVerifying: false,
            isResetting: false,
            mode: 'authenticator'
        });
        // Reset flags
        this.emailSentInSession = false;
        this.isSendingEmail = false;
        this.props.onClose();
    };

    // Handle mode change (radio button click)
    handleModeChange = (newMode) => {
        // If already in this mode, do nothing
        if (this.state.mode === newMode) {
            return;
        }

        this.setState({ mode: newMode });

        // If switching to email mode, send email OTP (only if not already sent)
        if (newMode === 'email') {
            this.sendEmailOtpOnce();
        }
    };

    // Send email OTP only once per session
    sendEmailOtpOnce = () => {
        // Check all flags to prevent duplicate calls
        if (this.emailSentInSession || this.isSendingEmail || this.props.isResendingEmailOtp) {
            console.log("Email already sent or in progress, skipping");
            return;
        }

        // Set synchronous flag immediately
        this.isSendingEmail = true;
        this.emailSentInSession = true;

        console.log("Sending email OTP (first time on mode switch)");

        if (this.props.onSendEmailOtp) {
            this.props.onSendEmailOtp();
        }

        // Reset the sending flag after a delay
        setTimeout(() => {
            this.isSendingEmail = false;
        }, 1000);
    };

    // Handle resend email OTP click (user explicitly clicks resend)
    handleResendEmailOtp = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Check if already resending
        if (this.isSendingEmail || this.props.isResendingEmailOtp) {
            console.log("Already resending, ignoring click");
            return;
        }

        // Set flag immediately
        this.isSendingEmail = true;

        console.log("Resending email OTP (user clicked resend)");

        if (this.props.onSendEmailOtp) {
            this.props.onSendEmailOtp(e);
        }

        // Reset the sending flag after a delay
        setTimeout(() => {
            this.isSendingEmail = false;
        }, 1000);
    };

    render() {
        const { show, isResendingEmailOtp = false, viaEmail } = this.props;
        const { otp, isVerifying, isResetting, mode } = this.state;
        const isOtpComplete = otp.every(digit => digit !== '');
        const isEmailMode = viaEmail || mode === 'email';

        return (
            <Modal 
                show={show} 
                onHide={this.handleClose}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header>
                    <Modal.Title className="cstm-card-title">
                        {isEmailMode ? 'Enter Email Verification Code' : 'Enter Verification Code'}
                    </Modal.Title>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={this.handleClose}
                        aria-label="Close"
                       
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                        <path d="M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z"/></svg></button>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <p className="mb-3">
                            {isEmailMode
                                ? 'Please enter the 6-digit verification code we sent to your email.'
                                : 'Please enter the 6-digit verification code from your authenticator app.'}
                        </p>

                        <div style={{marginTop: 8, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <label style={{marginRight: 12, display: 'flex', gap: '4px', alignItems: 'center'}}>
                                <input
                                    type="radio"
                                    name="otp_mode"
                                    value="authenticator"
                                    checked={mode === 'authenticator' && !viaEmail}
                                    onChange={() => this.handleModeChange('authenticator')}
                                    disabled={viaEmail || isVerifying || isResetting || isResendingEmailOtp}
                                />{' '}
                                Google Authenticator
                            </label>
                            <label  style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                                <input
                                    type="radio"
                                    name="otp_mode"
                                    value="email"
                                    checked={isEmailMode}
                                    onChange={() => this.handleModeChange('email')}
                                    disabled={isVerifying || isResetting || isResendingEmailOtp}
                                />{' '}
                                Email
                            </label>
                        </div>
                    </div>
                    
                    <div className="d-flex justify-content-center gap-2 mb-4">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={ref => this.otpRefs[index] = ref}
                                type="text"
                                className="form-control text-center fw-bold"
                                style={{ 
                                    width: '45px', 
                                    height: '45px', 
                                    fontSize: '18px',
                                    borderRadius: '8px'
                                }}
                                value={digit}
                                maxLength="1"
                                onChange={(e) => this.handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => this.handleKeyDown(index, e)}
                                onPaste={this.handlePaste}
                                disabled={isVerifying || isResetting}
                            />
                        ))}
                    </div>

                    <div className="text-center mb-3">
                        <small className="text-muted" style={{fontSize: '14px'}}>
                            {isEmailMode
                                ? 'Enter the code from the email sent to your registered address.'
                                : 'Enter the code from your Google Authenticator app.'}
                        </small>
                    </div>

                    <div>
                        <div className='text-center'>
                            {isEmailMode ? (
                                <>
                                    Didn't get the email?{' '}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={this.handleResendEmailOtp}
                                        disabled={isVerifying || isResetting || isResendingEmailOtp}
                                        style={{
                                            cursor: isResendingEmailOtp ? 'not-allowed' : 'pointer',
                                            opacity: isResendingEmailOtp ? 0.7 : 1
                                        }}
                                    >
                                        {isResendingEmailOtp ? (
                                            <>
                                                <span 
                                                    className="spinner-border spinner-border-sm me-1" 
                                                    role="status" 
                                                    aria-hidden="true"
                                                    style={{ width: '12px', height: '12px' }}
                                                ></span>
                                                Resending...
                                            </>
                                        ) : (
                                            'Resend email OTP'
                                        )}
                                    </button>
                                </>
                            ) : (
                                <>
                                    If you deleted or lost access to your authenticator app,{' '}
                                    <span 
                                        style={{
                                            cursor: (isVerifying || isResetting) ? 'not-allowed' : 'pointer', 
                                            color: '#009ef7', 
                                            fontWeight: 'bold',
                                            opacity: (isVerifying || isResetting) ? 0.7 : 1
                                        }} 
                                        onClick={() => {
                                            if (!isVerifying && !isResetting) {
                                                this.handleResetOtp();
                                            }
                                        }}
                                    >
                                        click here
                                    </span>{' '}
                                    to receive a 2FA reset link by email.
                                </>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-center flex-nowrap">
                    {/* <div> */}
                        <Button 
                            type="button"
                            variant="secondary" 
                            onClick={this.handleClose}
                            disabled={isVerifying}
                            className="me-2 w-100"
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="button"
                            className='w-100 '
                            variant="primary" 
                            onClick={this.handleVerifyOtp}
                            disabled={!isOtpComplete || isVerifying || isResetting}
                        >
                            {isVerifying ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Verifying...
                                </>
                            ) : (
                                'Verify Code'
                            )}
                        </Button>
                    {/* </div> */}
                </Modal.Footer>
            </Modal>
        );
    }
}

export default OTPModal;